import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Field } from '@navozyme/uikit/dist/atoms/Field';
import { DatePickerTimeInput } from '@navozyme/uikit/dist/molecules/DatePicker';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';
import { booleanOptionsForRadioGroup } from 'features/forms/helpers/random-form-helpers';

import {
  createPortcallStatusLog,
  CrewMember,
  FIRST_BERTH_STAY_ID,
  getBerth,
  getPassengers,
  getPortcallStatusLog,
  PortData,
  selectBerth,
  selectCrew,
  selectGeneralData,
  selectPassengers,
  selectPortCallData,
  selectStowaways,
  updateBerth,
  updateCrew,
  updatePassengers,
  updatePortCall,
  updatePortCallData,
} from '../../../slices/portCallSlice';

import Input from '../../../../forms/components/Input';
import MultiSelect from '../../../../forms/components/MultiSelect';
import NumberInputWithTwoDecimals from '../../../../forms/components/NumberInputWithTwoDecimals';
import RadioButtons from '../../../../forms/components/RadioButtonsCustom';
import SearchPortsDropdown from '../../../../forms/components/search-ports-dropdown/components/SearchPortsDropdown';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import { countryListAllIsoData } from '../../../../global/utils/countriesList';
import {
  isEmpty,
  isEmptyObject,
  MandatoryFieldMessages,
} from '../../../validations/FieldsValidation';

import '../../../styles/eta-and-etd.scss';
import '../../../styles/last-and-next-port.scss';
import '../../../styles/mandatory-check.scss';
import '../../../styles/purpose.scss';
import '../styles/crew-data.scss';
import '../styles/port-call-data.scss';
import '../styles/specify-crew-number.scss';

type PortCallDataFormProps = {
  formRef: React.RefObject<FormikProps<EtaAndEtdValues> | null>;
};

interface FinalValues {
  eta: { estimatedTime: string | null } | null;
  etd: { estimatedTime: string | null } | null;
}

export interface EtaAndEtdValues {
  eta: string | null;
  etd: string | null;

  lastPort?: PortData;
  nextPort?: PortData;
  atdLastPort: string | null;
  etaNextPort: string | null;
  /** Purpose */
  berthId: number;
  purposeOfCall: string | null;
  suppliesFreshWater: boolean;
  suppliesBunkering: boolean;
  suppliesIce: boolean;
  suppliesElectricity: boolean;
  /** DraftValues */
  arrivalDraft: number | null;
  departureDraft: number | null;
  /** Crew data */
  masterName?: string;
  masterSurname?: string;
  masterMiddlename?: string;
  masterNationality?: string;
  telephoneNumber?: string;
  /** Crew number */
  arrivalCrewQuantity: number;
  departureCrewQuantity: number;
  /** Passengers */
  arrivalPassengersQuantity: number;
  departurePassengersQuantity: number;
  arrivalStowaways: number;
  departureStowaways: number;
}

const validationSchema = Yup.object().shape(
  {
    eta: Yup.mixed().required('ETA is required'),
    etd: Yup.mixed().required('ETD is required'),
    lastPort: Yup.object().required('Last port is required'),
    atdLastPort: Yup.string().required('ATD is required'),

    nextPort: Yup.object().when('etaNextPort', (etaNextPort, schema) => {
      if (etaNextPort && !isEmptyObject(etaNextPort[0])) {
        return schema.required('Next port is required');
      } else {
        return schema.notRequired();
      }
    }),
    etaNextPort: Yup.string().when('nextPort', (nextPort, schema) => {
      if (nextPort && !isEmptyObject(nextPort[0])) {
        return schema.required('Next port ETA is required');
      } else {
        return schema.notRequired();
      }
    }),
    purposeOfCall: Yup.string().required('Purpose of call is required'),
    arrivalDraft: Yup.number()
      .required('Arrival draft is required')
      .min(0.1, 'Minimum number is 0.1')
      .max(49.99, 'This number is very high. Check it again.')
      .test('is-decimal', 'Only two decimal places are allowed', (value) => {
        if (value) {
          return /^\d+(\.\d{1,2})?$/.test(value.toString());
        }
        return true;
      }),
    departureDraft: Yup.number()
      .required('Departure draft is required')
      .min(0.1, 'Minimum number is 0.1')
      .max(49.99, 'This number is very high. Check it again.')
      .test('is-decimal', 'Only two decimal places are allowed', (value) => {
        if (value) {
          return /^\d+(\.\d{1,2})?$/.test(value.toString());
        }
        return true;
      }),

    /**crew data */
    masterName: Yup.string().required('Given name is required'),
    masterSurname: Yup.string().required('Family name is required'),
    masterNationality: Yup.string(),
    /** Crew number */
    arrivalCrewQuantity: Yup.number()
      .required('Arrival crew number is required')
      .min(0, 'Must be a positive number'),
    departureCrewQuantity: Yup.number()
      .required('Departure crew number is required')
      .min(0, 'Must be a positive number'),
    arrivalStowaways: Yup.number()
      .nullable()
      .min(0, 'Must be a positive number'),
    departureStowaways: Yup.number()
      .nullable()
      .min(0, 'Must be a positive number'),
  },
  [['nextPort', 'etaNextPort']]
);
const suppliesOptions = booleanOptionsForRadioGroup();

const multiSelectDropdownOptions = [
  { value: 'Alteration', label: 'Alteration' },
  { value: 'Alteration into a shipyard', label: 'Alteration into a shipyard' },
  { value: 'Biological stop', label: 'Biological stop' },
  { value: 'Cargo loading/unloading', label: 'Cargo loading/unloading' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Cruises', label: 'Cruises' },
  {
    value: 'Disembarkation of passengers',
    label: 'Disembarkation of passengers',
  },
  { value: 'Domestic traffic', label: 'Domestic traffic' },
  { value: 'Embarkation of passengers', label: 'Embarkation of passengers' },
  {
    value:
      'Fishing and artisanal vessels in fresh fish loading and unloading operations',
    label:
      'Fishing and artisanal vessels in fresh fish loading and unloading operations',
  },
  { value: 'Fishing inactivity', label: 'Fishing inactivity' },
  { value: 'Forced arrival', label: 'Forced arrival' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Lack of license', label: 'Lack of license' },
  { value: 'Launch', label: 'Launch' },
  { value: 'Legal deposit', label: 'Legal deposit' },
  { value: 'Major repair in shipyard', label: 'Major repair in shipyard' },
  { value: 'Official visit', label: 'Official visit' },
  { value: 'Provisioning', label: 'Provisioning' },
  { value: 'Provisioning in anchorage', label: 'Provisioning in anchorage' },
  { value: 'Provisioning in berth', label: 'Provisioning in berth' },
  {
    value: 'Provisioning in berth by barge',
    label: 'Provisioning in berth by barge',
  },
  {
    value: 'Provisioning of bunkering in anchorage',
    label: 'Provisioning of bunkering in anchorage',
  },
  {
    value: 'Provisioning of bunkering in berth',
    label: 'Provisioning of bunkering in berth',
  },
  {
    value: 'Repair afloat with crew personnel',
    label: 'Repair afloat with crew personnel',
  },
  {
    value: 'Repair at anchor with personal external to the crew',
    label: 'Repair at anchor with personal external to the crew',
  },
  { value: 'Scrapping', label: 'Scrapping' },
  { value: 'Scrapping in shipyard', label: 'Scrapping in shipyard' },
  { value: 'Ship in dry dock', label: 'Ship in dry dock' },
  { value: 'Shipyard construction', label: 'Shipyard construction' },
  { value: 'Transshipment of cargo', label: 'Transshipment of cargo' },
  {
    value: 'Vessel intended for mooring service',
    label: 'Vessel intended for mooring service',
  },
  {
    value: 'Vessel intended for pilotage service',
    label: 'Vessel intended for pilotage service',
  },
  {
    value: 'Vessel intended for towing service',
    label: 'Vessel intended for towing service',
  },
  {
    value: 'Vessels destined for works and dredging',
    label: 'Vessels destined for works and dredging',
  },
  {
    value: 'Others (submarine cable laying, oceanographic research, etc.)',
    label: 'Others (submarine cable laying, oceanographic research, etc.)',
  },
];

const PortCallDataForm = ({ formRef }: PortCallDataFormProps) => {
  const { formContextRef, setNoOfPage } = useContext(FormRefContext);
  const params = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const berth = useSelector(selectBerth);
  const firstBerthStay = berth.berthStays[0];

  useEffect(() => {
    params.uid && dispatch(getBerth(params.uid));
    setNoOfPage(firstBerthStay?.suppliesBunkering ? 5 : 4);
  }, [dispatch, firstBerthStay?.suppliesBunkering, params.uid, setNoOfPage]);

  const portCallData = useSelector(selectPortCallData);
  const generalPortcallData = useSelector(selectGeneralData);
  const { arrival: arrivalCrew, departure: departureCrew } =
    useSelector(selectCrew);
  const { arrival: arrivalPassengers, departure: departurePassengers } =
    useSelector(selectPassengers);
  const { arrivalStowaways, departureStowaways } = useSelector(selectStowaways);

  const masterArrival = arrivalCrew?.crewList?.find(
    (object) => object.rank === 'Master'
  );

  const masterName = masterArrival?.name;
  const masterSurname = masterArrival?.surname;
  const masterNationality = masterArrival?.nationality;
  const telephoneNumber = masterArrival?.telephoneNumber;

  const [masterEta, setMasterEta] = useState(
    portCallData?.data?.eta?.estimatedTime ?? null
  );
  const [masterEtd, setMasterEtd] = useState(
    portCallData?.data?.etd?.estimatedTime ?? null
  );

  const initialValues = {
    eta: portCallData?.data?.eta?.estimatedTime ?? null,
    etd: portCallData?.data?.etd?.estimatedTime ?? null,
    lastPort: portCallData?.data?.lastPort?.unlocode
      ? portCallData?.data?.lastPort
      : undefined,
    nextPort: portCallData?.data?.nextPort?.unlocode
      ? portCallData?.data?.nextPort
      : undefined,
    atdLastPort: portCallData?.data?.atdLastPort?.actualTime ?? null,
    etaNextPort: portCallData?.data?.etaNextPort?.estimatedTime ?? null,

    berthId: firstBerthStay?.berthId || FIRST_BERTH_STAY_ID,
    purposeOfCall: firstBerthStay?.purposeOfCall,
    suppliesFreshWater: firstBerthStay?.suppliesFreshWater || false,
    suppliesBunkering: firstBerthStay?.suppliesBunkering || false,
    suppliesIce: firstBerthStay?.suppliesIce || false,
    suppliesElectricity: firstBerthStay?.suppliesElectricity || false,

    arrivalDraft:
      typeof firstBerthStay?.arrivalDraft === 'number'
        ? firstBerthStay.arrivalDraft
        : null,
    departureDraft:
      typeof firstBerthStay?.departureDraft === 'number'
        ? firstBerthStay.departureDraft
        : null,

    /** Crew data */
    masterName: masterName,
    masterSurname: masterSurname,
    masterNationality: masterNationality,
    telephoneNumber: telephoneNumber,
    /** Crew number */
    arrivalCrewQuantity: arrivalCrew?.crewQuantity ?? 0,
    departureCrewQuantity: departureCrew?.crewQuantity ?? 0,
    arrivalPassengersQuantity: arrivalPassengers?.passengerQuantity ?? 0,
    departurePassengersQuantity: departurePassengers?.passengerQuantity ?? 0,
    arrivalStowaways: arrivalStowaways,
    departureStowaways: departureStowaways,
  };

  const handleSubmit = async (values: EtaAndEtdValues) => {
    if (params.uid) {
      const finalValues: FinalValues = {
        eta: null,
        etd: null,
      };
      finalValues.eta = {
        estimatedTime: values.eta,
      };
      finalValues.etd = {
        estimatedTime: values.etd,
      };
      const isSuccessUpdatePortCallData = await dispatch(
        updatePortCallData({
          uid: params.uid,
          data: {
            ...finalValues,
            lastPort: values.lastPort,
            nextPort: values.nextPort,
            atdLastPort: { actualTime: values.atdLastPort ?? null },
            etaNextPort: { estimatedTime: values.etaNextPort ?? null },
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });

      if (!isSuccessUpdatePortCallData) return;

      const finalBirthValues: {
        berthId: number;
        arrivalDraft: number | null;
        departureDraft: number | null;
        purposeOfCall: string | null;
        suppliesFreshWater: boolean;
        suppliesBunkering: boolean;
        suppliesIce: boolean;
        suppliesElectricity: boolean;
      } = {
        berthId: firstBerthStay?.berthId || FIRST_BERTH_STAY_ID,
        arrivalDraft: values.arrivalDraft ? Number(values.arrivalDraft) : null,
        departureDraft: values.departureDraft
          ? Number(values.departureDraft)
          : null,
        purposeOfCall: values.purposeOfCall ?? null,
        suppliesFreshWater: values.suppliesFreshWater,
        suppliesBunkering: values.suppliesBunkering,
        suppliesIce: values.suppliesIce,
        suppliesElectricity: values.suppliesElectricity,
      };

      const isSuccessUpdateBerth = await dispatch(
        updateBerth({
          uid: params.uid,
          data: { berthStay: { ...finalBirthValues } },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });

      if (!isSuccessUpdateBerth) return;
      /** Crew Data */
      const objIndex = arrivalCrew?.crewList?.findIndex(
        (cl) => cl?.rank === 'Master'
      );

      const masterDepartureIndex = departureCrew?.crewList?.findIndex(
        (object) => object.rank === 'Master'
      );

      const updateCrewData: {
        crewList: CrewMember[];
        departureCrewList: CrewMember[];
        arrivalCrewQuantity: number;
        departureCrewQuantity: number;
        hasCrewListChanged: boolean;
      } = {
        crewList: arrivalCrew!.crewList,
        departureCrewList: departureCrew!.crewList,
        arrivalCrewQuantity: 0,
        departureCrewQuantity: 0,
        hasCrewListChanged: false,
      };
      if (objIndex !== -1) {
        const originalObj = arrivalCrew!.crewList[objIndex!];

        // Check if Master data has changed
        const hasChanged =
          originalObj.name !== values.masterName ||
          originalObj.surname !== values.masterSurname ||
          originalObj.nationality !== values.masterNationality;

        updateCrewData.hasCrewListChanged = hasChanged;

        if (hasChanged) {
          const updatedObj = {
            ...originalObj,
            name: values.masterName,
            surname: values.masterSurname,
            nationality: values.masterNationality,
          };

          const filteredUpdatedObj = Object.fromEntries(
            Object.entries(updatedObj).filter(
              ([key]) => !(key === 'IDNature' && isEmpty(originalObj[key]))
            )
          );

          updateCrewData.crewList = [
            ...arrivalCrew!.crewList.slice(0, objIndex),
            filteredUpdatedObj as unknown as CrewMember,
            ...arrivalCrew!.crewList.slice(objIndex! + 1),
          ];

          if (masterDepartureIndex !== -1) {
            const originalDepartureObj =
              departureCrew!.crewList[masterDepartureIndex!];
            const updatedDepartureObj = {
              ...originalDepartureObj,
              name: values.masterName,
              surname: values.masterSurname,
            };

            updateCrewData.departureCrewList = [
              ...departureCrew!.crewList.slice(0, objIndex),
              updatedDepartureObj as unknown as CrewMember,
              ...departureCrew!.crewList.slice(objIndex! + 1),
            ];
          }
        }
      }

      updateCrewData.arrivalCrewQuantity =
        values.arrivalCrewQuantity || initialValues?.arrivalCrewQuantity || 0;
      updateCrewData.departureCrewQuantity =
        values.departureCrewQuantity ||
        initialValues?.departureCrewQuantity ||
        0;

      const isSuccessUpdateCrew = await dispatch(
        updateCrew({
          uid: params.uid,
          data: updateCrewData.hasCrewListChanged
            ? {
                crewList: updateCrewData.crewList,
                crewQuantity: updateCrewData.arrivalCrewQuantity,
              }
            : {
                crewQuantity: updateCrewData.arrivalCrewQuantity,
              },
          listType: 'arrival',
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else
          return dispatch(
            updateCrew({
              uid: params.uid!,
              data: {
                crewQuantity: updateCrewData.departureCrewQuantity,
                crewList: updateCrewData.departureCrewList,
              },
              listType: 'departure',
            })
          ).then((response) => {
            if (response.type && response.type.endsWith('/rejected'))
              return false;
            else return true;
          });
      });
      if (!isSuccessUpdateCrew) return;

      /** Passengers **/
      const isSuccessUpdatePassengers = await dispatch(
        updatePassengers({
          uid: params.uid,
          data: {
            arrivalPassengerQuantity: values.arrivalPassengersQuantity,
            departurePassengerQuantity: values.departurePassengersQuantity,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) {
          return false;
        } else {
          return true;
        }
      });
      if (!isSuccessUpdatePassengers) return;

      const isSuccessUpdatePortcall = await dispatch(
        updatePortCall({
          arrivalStowaway: values.arrivalStowaways,
          departureStowaway: values.departureStowaways,
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdatePortcall) return;

      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.GeneralDeclarationPortcallData,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateStatus) return;
      dispatch(getPortcallStatusLog(params.uid));
      dispatch(getPassengers({ uid: params.uid, listType: 'arrival' }));
      dispatch(getPassengers({ uid: params.uid, listType: 'departure' }));

      navigate(`/${params.uid}/vessel-data`);
    }
  };

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [
    formContextRef,
    formRef,
    portCallData,
    generalPortcallData,
    firstBerthStay,
  ]);

  useEffect(() => {
    params.uid && dispatch(getBerth(params.uid));
  }, [dispatch, params.uid]);

  const handleBunkeringOption = (value: boolean) => {
    setNoOfPage(value ? 5 : 4);
  };

  return (
    <div className="eta-and-etd">
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}>
        {({ values, setFieldValue }) => (
          <Form className="eta-etd-form">
            <div className="eta-etd-form__controls">
              <Field
                label="Estimated Time of Arrival (ETA) *"
                className="field_date_picker">
                <DatePickerTimeInput
                  key={initialValues?.eta + 'eta'}
                  name="eta"
                  sizeInput="large"
                  showTime={true}
                  defaultValue={
                    initialValues?.eta
                      ? initialValues.eta.replace('Z', '')
                      : null
                  }
                  action={(selected: {
                    inputDate: string;
                    inputTime: string;
                  }) => {
                    const selectedEta = `${selected.inputDate}T${selected.inputTime}:00Z`;
                    if (selected.inputDate && selected.inputTime) {
                      setFieldValue('eta', selectedEta);
                      setMasterEta(selectedEta);
                    } else {
                      setFieldValue('eta', null);
                      setMasterEta(null);
                    }
                  }}
                  hasError={
                    isEmpty(values?.eta) && formRef.current?.touched?.eta
                  }
                  errorMessage={MandatoryFieldMessages.RequiredField}
                />
              </Field>
              <Field
                label="Estimated Time of Departure (ETD) *"
                className="field_date_picker">
                <DatePickerTimeInput
                  key={initialValues?.eta ?? 'eta' + initialValues?.etd}
                  name="etd"
                  sizeInput="large"
                  minDate={
                    masterEta
                      ? new Date(masterEta.replace('Z', ''))
                      : initialValues?.eta
                        ? new Date(initialValues.eta.replace('Z', ''))
                        : new Date()
                  }
                  showTime={true}
                  defaultValue={
                    masterEtd
                      ? masterEtd.replace('Z', '')
                      : initialValues?.etd
                        ? initialValues.etd.replace('Z', '')
                        : null
                  }
                  action={(selected: {
                    inputDate: string;
                    inputTime: string;
                  }) => {
                    const selectedEtd = `${selected.inputDate}T${selected.inputTime}:00Z`;
                    if (selected.inputDate && selected.inputTime) {
                      setMasterEtd(selectedEtd);
                      setFieldValue('etd', selectedEtd);
                    } else {
                      setMasterEtd(null);
                      setFieldValue('etd', null);
                    }
                  }}
                  hasError={
                    isEmpty(values?.etd) && formRef.current?.touched?.etd
                  }
                  errorMessage={MandatoryFieldMessages.RequiredField}
                />
              </Field>
            </div>

            <hr className="port-call-data__divider" />
            <div className="last-and-next-port-form">
              <div className="last-and-next-port-form__controls">
                <div className="last-and-next-port-form__controls__row">
                  <SearchPortsDropdown
                    label="What was your last port? *"
                    name="lastPort"
                    dropdownID="lastPort"
                    placeholder="Last Port"
                  />

                  <Field
                    label="Actual Time of Departure (ATD) *"
                    className="field_date_picker_lastPort">
                    <DatePickerTimeInput
                      key={initialValues?.atdLastPort + 'atdLastPort'}
                      name="atdLastPort"
                      sizeInput="large"
                      showTime={true}
                      defaultValue={
                        initialValues?.atdLastPort
                          ? initialValues.atdLastPort.replace('Z', '')
                          : null
                      }
                      action={(selected: {
                        inputDate: string;
                        inputTime: string;
                      }) => {
                        const isoDate = `${selected.inputDate}T${selected.inputTime}:00Z`;
                        setFieldValue(
                          'atdLastPort',
                          selected.inputDate && selected.inputTime
                            ? isoDate
                            : null
                        );
                      }}
                      hasError={
                        isEmpty(values?.atdLastPort) &&
                        formRef.current?.touched?.atdLastPort
                      }
                      errorMessage={MandatoryFieldMessages.RequiredField}
                    />
                  </Field>
                </div>
                <div className="last-and-next-port-form__controls__row">
                  <SearchPortsDropdown
                    label="And the next port?"
                    name="nextPort"
                    dropdownID="nextPort"
                    placeholder="Next Port"
                  />
                  <Field
                    label="Estimated Time of Arrival (ETA)"
                    className="field_date_picker_nextPort">
                    <DatePickerTimeInput
                      key={initialValues?.etaNextPort + 'etaNextPort'}
                      name="etaNextPort"
                      sizeInput="large"
                      minDate={
                        portCallData?.data?.etd?.estimatedTime
                          ? new Date(portCallData?.data?.etd?.estimatedTime)
                          : null
                      }
                      showTime={true}
                      defaultValue={
                        initialValues?.etaNextPort
                          ? initialValues.etaNextPort.replace('Z', '')
                          : null
                      }
                      action={(selected: {
                        inputDate: string;
                        inputTime: string;
                      }) => {
                        const isoDate = `${selected.inputDate}T${selected.inputTime}:00Z`;
                        setFieldValue(
                          'etaNextPort',
                          selected.inputDate && selected.inputTime
                            ? isoDate
                            : null
                        );
                      }}
                      hasError={
                        !isEmptyObject(values?.nextPort) &&
                        isEmpty(values?.etaNextPort) &&
                        formRef.current?.touched?.etaNextPort
                      }
                      errorMessage={MandatoryFieldMessages.RequiredField}
                    />
                  </Field>
                </div>
              </div>
            </div>

            <hr className="port-call-data__divider" />
            <div className="purpose-form">
              <div className="purpose-form__controls">
                <MultiSelect
                  name="purposeOfCall"
                  options={multiSelectDropdownOptions}
                  isMulti={false}
                  label="Which is your primary purpose of call?*"
                  sublabel="Choose one option from the list below or type directly."
                  placeholder="Purpose of call"
                />
                <div className="supplies">
                  <div className="supplies__title">
                    <h3>Supplies</h3>
                    <p>
                      Please indicate if the vessel will receive the following
                      supplies during the port call.
                    </p>
                  </div>
                  <div className="supplies__list">
                    <RadioButtons
                      name="suppliesFreshWater"
                      label="Fresh water"
                      options={suppliesOptions}
                    />
                    <RadioButtons
                      name="suppliesBunkering"
                      label="Bunkering"
                      options={suppliesOptions}
                      onOptionClick={(value: boolean) =>
                        handleBunkeringOption(value)
                      }
                    />
                    <RadioButtons
                      name="suppliesIce"
                      label="Ice"
                      options={suppliesOptions}
                    />
                    <RadioButtons
                      name="suppliesElectricity"
                      label="Electricity"
                      options={suppliesOptions}
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr className="port-call-data__divider" />
            <h3 style={{ marginBottom: '16px' }}>
              What is your draft at arrival and departure?
            </h3>
            <div className="draft-form__controls">
              <NumberInputWithTwoDecimals
                name="arrivalDraft"
                min="0"
                placeholder="Arrival Draft"
                label="Arrival draft (m)*"
              />
              <NumberInputWithTwoDecimals
                name="departureDraft"
                min="0"
                placeholder="Departure Draft"
                label="Departure draft (m)*"
              />
            </div>

            <hr className="port-call-data__divider" />

            <div className="crew-master-data">
              <div className="crew-master-data__header">
                <h3>Crew *</h3>
                <p>Please confirm that your name and nationality is correct</p>
              </div>
              <div className="crew-master-data-form">
                <div className="crew-master-data-form__row">
                  <Input
                    name="masterName"
                    placeholder="Given name"
                    label="Given name*"
                  />
                  <Input
                    name="masterSurname"
                    placeholder="Family name"
                    label="Family name*"
                  />
                  <MultiSelect
                    name="masterNationality"
                    options={countryListAllIsoData}
                    isMulti={false}
                    label="Nationality"
                    placeholder="Choose"
                  />
                </div>
              </div>
            </div>
            <hr className="port-call-data__divider" />
            <div className="specify-crew-number-form">
              <div className="specify-crew-number-form__item">
                <label>How many crew members are on board?*</label>
                <div className="specify-crew-number-form__row">
                  <Input
                    name="arrivalCrewQuantity"
                    type="number"
                    min={0}
                    placeholder="0"
                    label="At arrival*"
                  />
                  <Input
                    name="departureCrewQuantity"
                    type="number"
                    min={0}
                    placeholder="0"
                    label="At departure*"
                  />
                </div>
              </div>
              <div className="specify-crew-number-form__item">
                <label>How many passengers are on board?</label>
                <div className="specify-crew-number-form__row">
                  <Input
                    name="arrivalPassengersQuantity"
                    type="number"
                    min={0}
                    placeholder="0"
                    label="At arrival"
                  />
                  <Input
                    name="departurePassengersQuantity"
                    type="number"
                    min={0}
                    placeholder="0"
                    label="At departure"
                  />
                </div>
              </div>
              <div className="specify-crew-number-form__item">
                <label>How many stowaways are on board?</label>
                <div className="specify-crew-number-form__row">
                  <Input
                    name="arrivalStowaways"
                    type="number"
                    min={0}
                    placeholder="0"
                    label="At arrival"
                  />
                  <Input
                    name="departureStowaways"
                    type="number"
                    min={0}
                    placeholder="0"
                    label="At departure"
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PortCallDataForm;
